import { Box, Typography } from "@mui/material";
import { memo } from "react";
import { QUIZ } from "@/theme/quizTokens";
import {
    useExperimentValue,
    QUIZ_PRESENTATION_EXPERIMENT_KEY,
} from "@/experiments/useExperimentValue";

type Props = {
    days: number;
    planLabel: string;
    selected: boolean;
    totalDiscount: number;
    totalFull: number;
    discountedPricePerDay: number;
    fullPricePerDay: number;
    onSelect: () => void;
    /** Temporarily not purchasable on new plans — muted, unselectable, badge shows lockedLabel. */
    locked?: boolean;
    lockedLabel?: string;
    /**
     * Plan-test arm "b" (src/experiments/planLadder.ts):
     * "Arrives every N days" subline, "Most popular" on 14-day + green
     * "Best value" on 28-day, and AA-contrast strike/per-day colours.
     */
    twoPlan?: boolean;
};

const BEST_VALUE_DAYS = 28;

/** PoP red used for struck-through prices in the Figma (1521:924). */
const STRIKE_RED = "#ff7873";

/** Plan-test arm "b": AA-passing replacements for STRIKE_RED and the grey per-day text. */
const STRIKE_RED_AA = "#d13f39";
const PER_DAY_MUTED_AA = "#6b5c50";

const MOST_POPULAR_DAYS = 14;

/** Phones narrower than this can't fit name + price at the xs sizes (they collided at 360px). */
const SMALL_PHONE = "@media (max-width: 374px)";

/** Selection blue for the ring + radio dot. */
const SELECT_BLUE = "#2fb3e3";

/**
 * A plan row inside the brown "Choose your plan" card (Figma 1521:924 mobile /
 * 1586:11883 desktop): vertically-centred radio + Kuillo plan name + RL Aqva
 * price with red struck-through original, and a cream per-day pill below.
 * Selection is marked with a thick #2FB3E3 ring + matching radio dot.
 */
function PlanCard({
    days,
    planLabel,
    selected,
    totalDiscount,
    totalFull,
    discountedPricePerDay,
    fullPricePerDay,
    onSelect,
    locked = false,
    lockedLabel,
    twoPlan = false,
}: Props) {
    // Experiment lever (presentation only, default = current copy).
    const bestValueLabel = useExperimentValue(
        QUIZ_PRESENTATION_EXPERIMENT_KEY,
        "plan_badge_text",
        "Best value"
    );

    const handleSelect = () => {
        if (locked) return;
        onSelect();
    };

    // The lock badge replaces "Best value" — a locked plan shouldn't advertise.
    const badge = locked
        ? lockedLabel
        : days === BEST_VALUE_DAYS
          ? bestValueLabel
          : twoPlan && days === MOST_POPULAR_DAYS
            ? "Most popular"
            : null;
    const showBadgeGap = !!badge;
    const badgeBg = locked
        ? QUIZ.color.cream3
        : twoPlan && days === BEST_VALUE_DAYS
          ? QUIZ.color.green
          : QUIZ.color.blue;

    return (
        <Box
            onClick={handleSelect}
            onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect();
            }}
            aria-disabled={locked || undefined}
            sx={{
                position: "relative",
                width: "100%",
                borderRadius: "999px",
                p: { xs: "12px 18px 12px 15px", md: "24px 40px 24px 28px" },
                cursor: locked ? "default" : "pointer",
                touchAction: "manipulation",
                userSelect: "none",
                WebkitTapHighlightColor: "transparent",
                background: QUIZ.color.white,
                border: `3px solid ${selected ? SELECT_BLUE : "transparent"}`,
                boxShadow: selected ? `0 0 0 1px ${SELECT_BLUE}` : "0 1px 3px rgba(0,0,0,0.08)",
                transition: "border 0.2s ease, box-shadow 0.2s ease",
                mb: { xs: "14px", md: "23px" },
                mt: showBadgeGap ? { xs: "8px", md: "14px" } : 0,
                display: "flex",
                alignItems: "center",
                gap: { xs: "12px", md: "16px" },
            }}
        >
            {badge && (
                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: -9, md: -16 },
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: badgeBg,
                        color: QUIZ.color.ink,
                        fontFamily: QUIZ.font.body,
                        fontWeight: 700,
                        fontSize: { xs: 10, md: 14 },
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        borderRadius: "999px",
                        px: { xs: "10px", md: "16px" },
                        py: { xs: "3px", md: "6px" },
                        whiteSpace: "nowrap",
                        opacity: 1,
                    }}
                >
                    {badge}
                </Box>
            )}

            {/* radio — vertically centred against the whole row (Figma) */}
            <Box
                sx={{
                    width: { xs: 17, md: 29 },
                    height: { xs: 17, md: 30 },
                    borderRadius: "50%",
                    flexShrink: 0,
                    border: `1.5px solid ${selected ? SELECT_BLUE : "#c9c2ba"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: locked ? 0.45 : 1,
                }}
            >
                {selected && (
                    <Box
                        sx={{
                            width: { xs: 9, md: 16 },
                            height: { xs: 9, md: 16 },
                            borderRadius: "50%",
                            bgcolor: SELECT_BLUE,
                        }}
                    />
                )}
            </Box>

            {/* content column */}
            <Box sx={{ flex: 1, minWidth: 0, opacity: locked ? 0.55 : 1 }}>
                {/* Top row: name + price */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.label,
                                fontSize: { xs: 17, md: 30 },
                                lineHeight: 1.245,
                                color: QUIZ.color.ink,
                                whiteSpace: "nowrap",
                                [SMALL_PHONE]: { fontSize: 15 },
                            }}
                        >
                            {planLabel}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", flexShrink: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.display,
                                fontWeight: 900,
                                fontSize: { xs: 21, md: 40 },
                                lineHeight: 1,
                                color: QUIZ.color.ink,
                                whiteSpace: "nowrap",
                                [SMALL_PHONE]: { fontSize: 19 },
                            }}
                        >
                            £{totalDiscount.toFixed(2)}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.display,
                                fontWeight: 900,
                                fontSize: { xs: 13, md: 25 },
                                [SMALL_PHONE]: { fontSize: 12 },
                                lineHeight: 1,
                                color: twoPlan ? STRIKE_RED_AA : STRIKE_RED,
                                textDecoration: "line-through",
                                whiteSpace: "nowrap",
                            }}
                        >
                            £{totalFull.toFixed(2)}
                        </Typography>
                    </Box>
                </Box>

                {/* Bottom row: cream per-day price pill, right-aligned */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: twoPlan ? "space-between" : "flex-end",
                        gap: "8px",
                        mt: { xs: "5px", md: "8px" },
                    }}
                >
                    {twoPlan && (
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.body,
                                fontSize: { xs: 12, md: 17 },
                                lineHeight: 1.2,
                                color: QUIZ.color.ink,
                                whiteSpace: "nowrap",
                                [SMALL_PHONE]: { fontSize: 11 },
                            }}
                        >
                            Arrives every {days} days
                        </Typography>
                    )}
                    <Box
                        sx={{
                            borderRadius: "999px",
                            background: QUIZ.color.cream3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "3px",
                            px: { xs: "12px", md: "20px" },
                            [SMALL_PHONE]: { px: "9px", fontSize: 10 },
                            py: { xs: "3px", md: "6px" },
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            fontFamily: QUIZ.font.body,
                            fontSize: { xs: 10.5, md: 17 },
                            color: QUIZ.color.ink,
                        }}
                    >
                        <span style={{ fontWeight: 700 }}>£{discountedPricePerDay.toFixed(2)}</span>
                        <span>/</span>
                        <span style={{ color: twoPlan ? PER_DAY_MUTED_AA : "#8e8e8e", textDecoration: "line-through" }}>
                            £{fullPricePerDay.toFixed(2)}
                        </span>
                        <span style={{ marginLeft: "2px" }}>per day</span>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default memo(PlanCard);
